import { useEffect, useId, useState } from "react";
import { Download } from "lucide-react";

import { api, errMsg } from "../../../../lib/api.js";
import { logError } from "../../../../lib/logger.js";
import { useToast } from "../../../../context/ToastContext.js";
import { AsyncButton } from "../../../../components/ui/async-button.js";
import { Badge } from "../../../../components/ui/badge.js";
import { Button } from "../../../../components/ui/button.js";
import { Checkbox } from "../../../../components/ui/checkbox.js";
import { Label } from "../../../../components/ui/label.js";
import { Textarea } from "../../../../components/ui/textarea.js";
import { useConfirm } from "../../../../components/ui/confirm-dialog.js";
import { useMe } from "../../../../context/MeContext.js";
import { LockedNotice, SubPanel, fmtDate } from "../../shared.js";
import type { ProbationFormQuestion, ProbationInfo, ReviewCapabilities } from "../../types.js";

type AnchorProbation = Extract<ProbationInfo, { isThisReview: true }>;

// "New Employee First 30 Days Review": opens 30 days after joining. The employee
// answers (drafts as they go), then ticks to sign — signing submits and locks
// it. The reporting manager then ticks to sign, which also produces the signed
// PDF that only HR can download.
export default function ProbationFormBlock({
  reviewId,
  employeeName,
  probation,
  caps,
  onChanged,
}: {
  reviewId: string;
  employeeName: string;
  probation: AnchorProbation;
  caps: ReviewCapabilities;
  onChanged: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const me = useMe();
  const form = probation.form;
  const [answers, setAnswers] = useState<Record<string, string>>({});

  useEffect(() => {
    setAnswers(Object.fromEntries(form.questions.map((q) => [q.key, q.answer ?? ""])));
  }, [form.questions]);

  const payload = () => form.questions.map((q) => ({ key: q.key, answer: answers[q.key] ?? "" }));
  const allAnswered = form.questions.every((q) => (answers[q.key] ?? "").trim() !== "");

  async function call(label: string, request: () => Promise<unknown>, success: string) {
    try {
      await request();
      toast.show(success, "success");
      onChanged();
    } catch (e) {
      logError(`ProbationFormBlock.${label}`, e);
      toast.show(errMsg(e) ?? "Could not save", "error");
      throw e;
    }
  }

  // Ticking a signature box asks first, then signs. Resolves false on cancel.
  async function sign(who: "employee" | "manager"): Promise<boolean> {
    // The dialog shows the signature exactly as it will be saved: the signer's
    // name in the signature style, with today's date below it.
    const signer = who === "employee" ? employeeName : me.fullName || "Reporting manager";
    const ok = await confirm(
      who === "employee"
        ? {
            title: "Sign and submit your 30-day review?",
            description: (
              <SignaturePreview
                name={signer}
                note="This is added as your signature and the form locks. Your manager is notified."
              />
            ),
            actionText: "Sign and submit",
            tone: "success",
          }
        : {
            title: "Sign this 30-day review?",
            description: (
              <SignaturePreview
                name={signer}
                note="This is added as the reporting manager's signature and the signed PDF is saved for HR."
              />
            ),
            actionText: "Sign",
            tone: "success",
          },
    );
    if (!ok) return false;
    await call(
      who === "employee" ? "employeeSign" : "managerSign",
      () =>
        who === "employee"
          ? api.post(`/performance/reviews/${reviewId}/probation-form/submit`, { answers: payload(), signed: true })
          : api.post(`/performance/reviews/${reviewId}/probation-form/review`, { signed: true }),
      who === "employee" ? "Signed and submitted" : "Signed",
    );
    return true;
  }

  async function downloadPdf() {
    // Open the tab synchronously so the popup blocker allows it.
    const tab = window.open("", "_blank");
    try {
      const { data } = await api.get<{ url: string }>(`/performance/reviews/${reviewId}/probation-form/pdf`);
      if (tab) tab.location.href = data.url;
      else window.location.href = data.url;
    } catch (e) {
      tab?.close();
      logError("ProbationFormBlock.pdf", e);
      toast.show(errMsg(e) ?? "Could not open the PDF", "error");
    }
  }

  const status = form.reviewedAt ? (
    <Badge tone="success">Signed by both</Badge>
  ) : form.submittedAt ? (
    <Badge tone="info">Awaiting manager signature</Badge>
  ) : form.isOpen ? (
    <Badge tone="warning">{form.savedAt ? "Draft saved" : "Pending"}</Badge>
  ) : (
    <Badge tone="neutral">Opens {fmtDate(form.opensOn)}</Badge>
  );

  const actions = (
    <>
      {status}
      {caps.canDownloadProbationPdf && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-surface-500"
          title="Download the signed PDF"
          aria-label="Download the signed PDF"
          onClick={() => void downloadPdf()}
        >
          <Download size={15} />
        </Button>
      )}
    </>
  );

  const filling = form.isOpen && caps.canFillProbationForm;

  return (
    <SubPanel title="First 30 days review" actions={actions} collapsible>
      {!form.isOpen ? (
        <div className="flex flex-col gap-4">
          <LockedNotice>
            {caps.isEmployee
              ? `Your 30-day review opens on ${fmtDate(form.opensOn)}. You'll get a notification.`
              : `This form opens on ${fmtDate(form.opensOn)}, 30 days after joining. The employee will get a notification.`}
          </LockedNotice>
          <QuestionRows questions={form.questions} mode="blank" />
        </div>
      ) : filling ? (
        <QuestionRows
          questions={form.questions}
          mode="edit"
          answers={answers}
          onAnswer={(key, value) => setAnswers((a) => ({ ...a, [key]: value }))}
        />
      ) : form.submittedAt ? (
        <QuestionRows questions={form.questions} mode="read" />
      ) : (
        // HR / the manager see the blank form until the employee signs; a
        // draft stays private to the employee.
        <div className="flex flex-col gap-4">
          <LockedNotice>
            {caps.isEmployee
              ? "Your 30-day review was not signed before the probation closed."
              : "The employee has not signed their 30-day review yet."}
          </LockedNotice>
          <QuestionRows questions={form.questions} mode="blank" />
        </div>
      )}

      {filling && (
        <div className="mt-4 flex justify-end">
          <AsyncButton
            variant="outline"
            animated={false}
            size="sm"
            onClick={() =>
              call(
                "saveDraft",
                () => api.put(`/performance/reviews/${reviewId}/probation-form`, { answers: payload() }),
                "Draft saved",
              )
            }
          >
            Save draft
          </AsyncButton>
        </div>
      )}

      {form.isOpen && (
        <div className="mt-5 grid gap-4 border-t border-surface-100 pt-4 sm:grid-cols-2">
          <SignatureBox
            role="Employee"
            signedName={form.submittedAt ? form.employeeSignedName ?? employeeName : null}
            signedAt={form.submittedAt}
            canSign={filling}
            signLabel={`I, ${employeeName}, confirm these answers are my own.`}
            disabledReason={allAnswered ? null : "Answer every question to sign."}
            onSign={() => sign("employee")}
          />
          <SignatureBox
            role="Reporting manager"
            signedName={form.reviewedAt ? form.managerSignedName : null}
            signedAt={form.reviewedAt}
            canSign={caps.canReviewProbationForm}
            signLabel="I have reviewed these answers and sign as the reporting manager."
            disabledReason={null}
            onSign={() => sign("manager")}
          />
        </div>
      )}

      {caps.canReopenProbationForm && (
        <div className="mt-4 flex justify-end">
          <AsyncButton
            variant="outline"
            animated={false}
            size="sm"
            confirm={{
              title: "Reopen the 30-day review?",
              description: "Both signatures are cleared. The employee can edit and sign again, then the manager signs again.",
              actionText: "Reopen",
              tone: "warning",
            }}
            onClick={() =>
              call("reopen", () => api.post(`/performance/reviews/${reviewId}/probation-form/reopen`), "Form reopened")
            }
          >
            Reopen for the employee
          </AsyncButton>
        </div>
      )}
    </SubPanel>
  );
}

// Question on the left, response on the right (stacked on narrow screens).
// `blank` draws empty response boxes; `read` shows the submitted answers.
function QuestionRows({
  questions,
  mode,
  answers,
  onAnswer,
}: {
  questions: ProbationFormQuestion[];
  mode: "edit" | "read" | "blank";
  answers?: Record<string, string>;
  onAnswer?: (key: string, value: string) => void;
}) {
  return (
    <div className="flex flex-col divide-y divide-surface-100 text-[13px]">
      {questions.map((q, i) => {
        const id = `probation-q-${q.key}`;
        return (
          <div key={q.key} className="grid gap-2 py-3 first:pt-0 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-4">
            <label htmlFor={mode === "edit" ? id : undefined} className="font-medium text-surface-700">
              {i + 1}. {q.question}
            </label>
            {mode === "edit" ? (
              <Textarea
                id={id}
                rows={2}
                value={answers?.[q.key] ?? ""}
                onChange={(e) => onAnswer?.(q.key, e.target.value)}
              />
            ) : mode === "read" ? (
              <p className="whitespace-pre-wrap">{q.answer || ""}</p>
            ) : (
              <div className="h-9 rounded-md border border-dashed border-surface-200" aria-label="No response yet" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// The signature as it will be saved, shown inside the sign confirmation.
function SignaturePreview({ name, note }: { name: string; note: string }) {
  return (
    <span className="flex flex-col items-center gap-3">
      <span className="flex w-full max-w-64 flex-col items-center">
        <span className="font-serif text-2xl text-brand-700 italic">{name}</span>
        <span className="mt-1 w-full border-t border-surface-300" />
        <span className="mt-1 text-[12px] text-surface-500">Signed {fmtDate(new Date().toISOString())}</span>
      </span>
      <span>{note}</span>
    </span>
  );
}

// One side of the signature strip: the signed name (signature style) with the
// date below it, the viewer's sign box when it is their turn, or "awaiting".
function SignatureBox({
  role,
  signedName,
  signedAt,
  canSign,
  signLabel,
  disabledReason,
  onSign,
}: {
  role: string;
  signedName: string | null;
  signedAt: string | null;
  canSign: boolean;
  signLabel: string;
  disabledReason: string | null;
  onSign: () => Promise<boolean>;
}) {
  const id = useId();
  const [busy, setBusy] = useState(false);

  async function onChecked(checked: boolean) {
    if (!checked || busy) return;
    setBusy(true);
    try {
      await onSign();
    } catch {
      // The error toast is already shown; the box unticks so they can retry.
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[11px] font-medium tracking-wide text-surface-400 uppercase">{role}</p>
      {signedName ? (
        <div>
          <p className="font-serif text-xl text-brand-700 italic">{signedName}</p>
          <div className="mt-1 border-t border-surface-300" />
          <p className="mt-1 text-[12px] text-surface-500">Signed {fmtDate(signedAt)}</p>
        </div>
      ) : canSign ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-2">
            <Checkbox
              id={id}
              checked={busy}
              disabled={busy || disabledReason != null}
              onCheckedChange={(c) => void onChecked(c === true)}
              className="mt-0.5"
            />
            <Label htmlFor={id} className="text-[13px] leading-snug font-normal">
              {signLabel}
            </Label>
          </div>
          {disabledReason && <p className="text-[12px] text-surface-400">{disabledReason}</p>}
        </div>
      ) : (
        <p className="text-[13px] text-surface-400">Awaiting signature</p>
      )}
    </div>
  );
}
